import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  SearchX,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge, BadgeColor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Typography from "@/components/ui/typography";

import { useCancelSearch, useConfirmSearch, useCreateSearch, useSearchLeads,useSearchStatus, useSearchSuggestion } from "../hooks/useLeads";
import { Lead, LeadSearch } from "../types/leads";

const MATCH_BADGE: Record<Lead["matchConfidence"], { label: string; color: BadgeColor }> = {
  high: { label: "High", color: "green" },
  medium: { label: "Medium", color: "yellow" },
  low: { label: "Low", color: "red" },
};

type FlowStep = "query" | "preview";

interface InlineSearchFlowProps {
  active: boolean;
  onClose: () => void;
  resumeSearch?: LeadSearch;
  creditsRemaining: number;
}

const InlineSearchFlow = ({
  active,
  onClose,
  resumeSearch,
  creditsRemaining,
}: InlineSearchFlowProps) => {
  const [step, setStep] = useState<FlowStep>("query");
  const [query, setQuery] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [fullSearchCount, setFullSearchCount] = useState(25);
  const [hasRestoredSearch, setHasRestoredSearch] = useState(false);
  // Tracks whether user dismissed the flow — prevents resumeSearch from reopening it
  const [dismissed, setDismissed] = useState(false);

  const suggestionMutation = useSearchSuggestion();
  const createSearchMutation = useCreateSearch();
  const confirmSearchMutation = useConfirmSearch();
  const cancelSearchMutation = useCancelSearch();
  const { data: statusData } = useSearchStatus(
    step === "preview" && searchId ? searchId : null,
  );
  const previewReady = statusData?.status === "preview_ready";
  const { data: previewLeads, isLoading: isLoadingLeads } = useSearchLeads(
    previewReady ? searchId : null,
  );

  // Restore in-progress search on mount / page refresh
  useEffect(() => {
    if (resumeSearch && !hasRestoredSearch && !dismissed) {
      setSearchId(resumeSearch.id);
      setQuery(resumeSearch.query);
      setStep("preview");
      setHasRestoredSearch(true);
    }
  }, [resumeSearch, hasRestoredSearch, dismissed]);

  const fetchSuggestion = useCallback(() => {
    suggestionMutation.mutate(undefined, {
      onSuccess: (data) => {
        setQuery(data.query);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            const len = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(len, len);
          }
        }, 50);
      },
    });
  }, [suggestionMutation]);

  // Fetch AI suggestion when user clicks "Start Search"
  useEffect(() => {
    if (active && !resumeSearch) {
      setStep("query");
      setSearchId(null);
      setFullSearchCount(25);
      setHasRestoredSearch(false);
      setDismissed(false);
      setQuery("");
      fetchSuggestion();
    }
  }, [active, resumeSearch]);  

  const handleRunPreview = useCallback(async () => {
    try {
      const search = await createSearchMutation.mutateAsync(query);
      setSearchId(search.id);
      setStep("preview");
    } catch {
      toast.error("Failed to start search. Please try again.");
    }
  }, [query, createSearchMutation]);

  const handleConfirmFullSearch = useCallback(async () => {
    if (!searchId) return;
    try {
      await confirmSearchMutation.mutateAsync({
        searchId,
        matchLimit: fullSearchCount,
      });
      // Reset flow state so it collapses cleanly
      setSearchId(null);
      setStep("query");
      setDismissed(true);
      toast.success("Search started!", {
        description: "Your lead search is running in the background. New leads will appear automatically.",
      });
      onClose();
    } catch {
      toast.error("Failed to confirm search. Please try again.");
    }
  }, [searchId, fullSearchCount, confirmSearchMutation, onClose]);

  const handleDismiss = useCallback(() => {
    // Cancel the active search in the DB so it doesn't resurface on refresh
    if (searchId) {
      cancelSearchMutation.mutate(searchId);
    }
    setDismissed(true);
    onClose();
  }, [searchId, cancelSearchMutation, onClose]);

  const handleNewSearch = useCallback(() => {
    // Cancel the current search so it doesn't linger as in-progress
    if (searchId) {
      cancelSearchMutation.mutate(searchId);
    }
    setSearchId(null);
    setStep("query");
    setQuery("");
    setFullSearchCount(25);
    fetchSuggestion();
  }, [searchId, cancelSearchMutation, fetchSuggestion]);

  const isLoadingPreview = createSearchMutation.isPending ||
    (!!searchId && !previewReady && statusData?.status !== "completed" && statusData?.status !== "failed") ||
    (previewReady && isLoadingLeads);

  const leads = previewLeads ?? [];
  const maxLeads = Math.min(creditsRemaining, 50);
  const noPreviewResults = previewReady && !isLoadingLeads && leads.length === 0;
  const canConfirm =
    !confirmSearchMutation.isPending &&
    fullSearchCount >= 1 &&
    creditsRemaining >= 1;

  // Don't render if dismissed
  const isVisible = (active || (!!resumeSearch && !dismissed)) && !dismissed;

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-300 ease-in-out ${
        isVisible
          ? "border-[#e8e8e8] max-h-[1000px] opacity-100 p-5 space-y-4"
          : "border-transparent max-h-0 opacity-0 p-0"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Typography size="l2" className="font-semibold text-[#1e1e24]">
            {step === "query" ? "New Search" : "Preview Results"}
          </Typography>
          <Typography size="b2" className="text-[#767677] mt-1">
            {step === "query"
              ? "Edit the suggested query or write your own. Preview is free — no credits used."
              : null}
          </Typography>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded-md text-[#a4a4a4] hover:text-[#494949] hover:bg-[#f0f0f0] transition-colors flex-shrink-0"
          aria-label="Close search"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Step: Query */}
      {step === "query" && (
        <>
          <div className="relative">
            {suggestionMutation.isPending ? (
              <div className="min-h-[100px] flex items-center justify-center gap-2.5 rounded-md border border-[#e8e8e8] bg-[#fafafa]">
                <Sparkles className="h-4 w-4 text-sds-purple-80 animate-pulse" />
                <span className="text-sm text-[#767677]">Generating AI suggestion...</span>
              </div>
            ) : (
              <Textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="min-h-[100px] resize-none text-sm leading-relaxed"
                placeholder="Describe what kind of leads you're looking for..."
              />
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-[12px] text-[#767677] hover:text-[#494949] gap-1.5 self-start"
              onClick={fetchSuggestion}
              disabled={suggestionMutation.isPending}
            >
              {suggestionMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Suggest another
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                className="h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent"
                onClick={handleDismiss}
              >
                Cancel
              </Button>
              <Button
                className="bg-sds-purple-100 hover:bg-sds-purple-80 text-white rounded-lg px-5 h-9 flex-1 sm:flex-initial"
                onClick={handleRunPreview}
                disabled={isLoadingPreview || suggestionMutation.isPending || !query.trim() || query.trim().length < 3}
              >
                {isLoadingPreview ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating preview...
                  </>
                ) : (
                  <>
                    Preview Results
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Step: Preview */}
      {step === "preview" && (
        <>
          {isLoadingPreview ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-5 w-5 text-sds-purple-80 animate-spin" />
              <Typography size="b2" className="text-[#767677]">
                Finding preview leads...
              </Typography>
              <Typography size="b2" className="text-[#a4a4a4] text-xs">
                This can take up to a few minutes
              </Typography>
            </div>
          ) : statusData?.status === "failed" ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Typography size="b2" className="text-red-500">
                Search failed: {statusData.error || "Unknown error"}
              </Typography>
              <Button
                variant="ghost"
                className="h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent"
                onClick={handleNewSearch}
              >
                Try a Different Search
              </Button>
            </div>
          ) : noPreviewResults ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <SearchX className="h-8 w-8 text-[#a4a4a4]" />
              <div className="text-center space-y-1">
                <Typography size="b2" className="font-semibold text-[#494949]">
                  No matching leads found
                </Typography>
                <Typography size="b2" className="text-[#767677] text-sm max-w-sm">
                  We searched {statusData?.totalGenerated ?? 0} candidates but none matched your criteria. Try broadening your search or using different keywords.
                </Typography>
              </div>
              <Button
                className="bg-sds-purple-100 hover:bg-sds-purple-80 text-white rounded-lg px-5 h-9"
                onClick={handleNewSearch}
              >
                New Search
              </Button>
            </div>
          ) : (
            <>
              <Typography size="b2" className="text-[#767677]">
                Review these results, then choose how many more to find. Only matched leads use credits.
              </Typography>

              {/* Results table */}
              <div className="flex flex-col">
                <div className="flex items-center px-4 pb-2 border-b border-[#f0f0f0]">
                  <div className="flex-1 min-w-0 text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4]">
                    Company
                  </div>
                  <div className="flex-1 min-w-0 px-4 hidden sm:block text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4]">
                    Contact
                  </div>
                  <div className="shrink-0 w-[160px] px-3 hidden md:block text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4]">
                    LinkedIn
                  </div>
                  <div className="shrink-0 w-auto sm:w-[70px] flex justify-end text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4]">
                    Match
                  </div>
                </div>

                {leads.map((lead) => {
                  const match = MATCH_BADGE[lead.matchConfidence];
                  return (
                    <div
                      key={lead.id}
                      className="flex items-center px-4 py-3.5 border-b border-[#f0f0f0]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#1e1e24] tracking-[-0.3px] truncate">
                          {lead.companyName}
                        </div>
                        <a
                          href={lead.companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-[#a4a4a4] hover:text-sds-purple-100 mt-0.5 truncate inline-flex items-center gap-0.5 transition-colors"
                        >
                          {lead.companyUrl.replace("https://", "")}
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                        {/* Mobile: contact inline under company */}
                        <div className="text-[12px] text-[#767677] truncate mt-0.5 sm:hidden">
                          {lead.contactName} · {lead.contactTitle}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 px-4 hidden sm:block">
                        <div className="text-[13px] font-medium text-[#494949] tracking-[-0.3px] truncate">
                          {lead.contactName}
                        </div>
                        <div className="text-[11px] text-[#a4a4a4] mt-0.5 truncate">
                          {lead.contactTitle}
                        </div>
                      </div>
                      <div className="shrink-0 w-[160px] px-3 hidden md:block">
                        <a
                          href={lead.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-[#a4a4a4] hover:text-sds-purple-100 transition-colors truncate block"
                        >
                          {lead.linkedinUrl?.replace("https://linkedin.com/", "") || "—"}
                        </a>
                      </div>
                      <div className="shrink-0 w-auto sm:w-[70px] flex justify-end ml-2">
                        <Badge variant="opaque" color={match.color} className="text-[11px]">
                          {match.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Decision + CTAs */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pt-2">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#767677]">Up to</span>
                    <Input
                      type="number"
                      value={fullSearchCount}
                      onChange={(e) =>
                        setFullSearchCount(
                          Math.max(1, Math.min(maxLeads, Number(e.target.value)))
                        )
                      }
                      className="w-20 h-9 text-sm text-center border-[#e8e8e8]"
                      min={1}
                      max={maxLeads}
                      disabled={creditsRemaining < 1}
                    />
                    <span className="text-sm text-[#494949]">additional leads</span>
                  </div>
                  {creditsRemaining < 1 ? (
                    <p className="text-[11px] text-red-500">
                      No credits remaining — request more to continue
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#a4a4a4]">
                      {creditsRemaining} credits remaining
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-stretch sm:items-end gap-1.5">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      className="h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent"
                      onClick={handleNewSearch}
                    >
                      New Search
                    </Button>
                    <Button
                      className="bg-sds-purple-100 hover:bg-sds-purple-80 text-white rounded-lg px-5 h-9 flex-1 sm:flex-initial"
                      onClick={handleConfirmFullSearch}
                      disabled={!canConfirm}
                    >
                      {confirmSearchMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      Find up to {fullSearchCount} Leads
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                  <span className="text-[11px] text-[#a4a4a4] text-right">Runs in background</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InlineSearchFlow;
